// Setup → Users: /api/setup/users (apps/server/src/setupUsers.mjs). Admins only.
import type { AdminMember, AdminRole } from '@olytic/foundation/admin';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export const USERS_API = '/api/setup/users';

export interface SetupUser extends AdminMember {
  createdAt?: string | undefined;
  /** ISO timestamp; present while they are deactivated. */
  deactivatedAt?: string | undefined;
}

/** A role, with what it may do (apps/server/src/access.mjs `ROLES`), e.g. `be_impersonated`. */
export interface SetupRole extends AdminRole {
  capabilities: string[];
}

export interface UsersList {
  users: SetupUser[];
  roles: SetupRole[];
}

export interface AddUsersResult {
  added: SetupUser[];
  /** Addresses that already had a user; left as they were. */
  existing: string[];
}

export class UsersApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

function parseUser(value: unknown): SetupUser {
  if (!isObject(value) || typeof value.id !== 'string' || typeof value.email !== 'string' || typeof value.role !== 'string') {
    throw new UsersApiError('The server sent a user the page cannot read.', 500);
  }
  return {
    id: value.id,
    name: typeof value.name === 'string' ? value.name : '',
    email: value.email,
    role: value.role,
    status: value.status === 'invited' || value.status === 'deactivated' ? value.status : 'active',
    ...(typeof value.createdAt === 'string' ? { createdAt: value.createdAt } : {}),
    ...(typeof value.deactivatedAt === 'string' ? { deactivatedAt: value.deactivatedAt } : {}),
  };
}

function parseRoles(value: unknown): SetupRole[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((r) =>
    isObject(r) && typeof r.key === 'string' && typeof r.label === 'string'
      ? [{
          key: r.key,
          label: r.label,
          ...(typeof r.description === 'string' ? { description: r.description } : {}),
          capabilities: Array.isArray(r.capabilities) ? r.capabilities.filter((c): c is string => typeof c === 'string') : [],
        }]
      : [],
  );
}

/** Whether `role` (a role key) may do `capability`, by the roles the server sent. */
export const roleCan = (roles: SetupRole[], role: string, capability: string): boolean =>
  roles.find((r) => r.key === role)?.capabilities.includes(capability) ?? false;

async function send(fetcher: Fetch, url: string, init: RequestInit, fallback: string): Promise<Record<string, unknown>> {
  const response = await fetcher(url, init);
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // Not JSON: the fallback message below.
  }
  if (!response.ok) {
    const message = isObject(payload) && typeof payload.error === 'string' ? payload.error : fallback;
    throw new UsersApiError(message, response.status);
  }
  if (!isObject(payload)) throw new UsersApiError(fallback, response.status);
  return payload;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

export async function listUsers(fetcher: Fetch): Promise<UsersList> {
  const payload = await send(fetcher, USERS_API, {}, 'Unable to load users.');
  return { users: Array.isArray(payload.users) ? payload.users.map(parseUser) : [], roles: parseRoles(payload.roles) };
}

export async function getUser(fetcher: Fetch, id: string): Promise<{ user: SetupUser; roles: SetupRole[] }> {
  const payload = await send(fetcher, `${USERS_API}/${encodeURIComponent(id)}`, {}, 'Unable to load this user.');
  return { user: parseUser(payload.user), roles: parseRoles(payload.roles) };
}

export async function addUsers(fetcher: Fetch, body: { emails: string[]; role: string; name?: string }): Promise<AddUsersResult> {
  const payload = await send(fetcher, USERS_API, json('POST', body), 'Unable to add users.');
  return {
    added: Array.isArray(payload.added) ? payload.added.map(parseUser) : [],
    existing: Array.isArray(payload.existing) ? payload.existing.filter((e): e is string => typeof e === 'string') : [],
  };
}

export interface UserPatch {
  role?: string;
  name?: string | null;
  /** false deactivates (no sign-in, no projects), true reactivates. */
  active?: boolean;
}

export async function updateUser(fetcher: Fetch, id: string, patch: UserPatch): Promise<SetupUser> {
  const payload = await send(fetcher, `${USERS_API}/${encodeURIComponent(id)}`, json('PATCH', patch), 'Unable to save this user.');
  return parseUser(payload.user);
}
